import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorBoundarySuspense } from '@/components/error-boundary-suspense';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { LotSizingSupplierTableSkeleton } from '@/features/lot-sizing/skeleton/lot-sizing-supplier-table-skeleton';
import { GET_LOT_SIZING_KEY } from '@/keys';
import { CriteriaFormSkeleton } from './component/criteria-form/criteria-form-skeleton';
import { LotSizingCriteria } from './lot-sizing-criteria';
import { LotSizingFillRate } from './lot-sizing-fill-rate';
import { LotSizingHeader } from './lot-sizing-header';
import { LotSizingInfo } from './lot-sizing-info';
import { LotSizingPurchaseOrderDraftProvider } from './lot-sizing-purchase-order-draft-context';
import { LotSizingRefsTable } from './lot-sizing-table';
import { LotSizingHeaderSkeleton } from './skeleton/lot-sizing-header-skeleton';
import { LotSizingInfoSkeleton } from './skeleton/lot-sizing-info-skeleton';

type LotSizingProps = {
  id: string;
};

export function LotSizing({ id }: LotSizingProps) {
  const queryKey = useMemo(() => [...GET_LOT_SIZING_KEY, id] as const, [id]);
  const { t } = useTranslation('lotSizing');

  return (
    <LotSizingPurchaseOrderDraftProvider key={id}>
      <div className='flex flex-col gap-6'>
        <ErrorBoundarySuspense
          errorFallbackWrapper={<div className='mt-4 w-full max-w-md' />}
          loadingFallback={<LotSizingHeaderSkeleton />}
          message={t('unableToLoad')}
          queryKey={queryKey}
        >
          <LotSizingHeader id={id} />
        </ErrorBoundarySuspense>

        <div className='grid grid-cols-[1fr_2fr] gap-6'>
          <ErrorBoundarySuspense
            errorFallbackWrapper={<div className='mt-4 w-full max-w-md' />}
            loadingFallback={
              <Card>
                <CardHeader />
                <CardContent>
                  <CriteriaFormSkeleton />
                </CardContent>
              </Card>
            }
            message={t('unableToLoad')}
            queryKey={queryKey}
          >
            <LotSizingCriteria lotSizingId={id} />
          </ErrorBoundarySuspense>
          <div className='flex flex-col gap-6'>
            <ErrorBoundarySuspense
              errorFallbackWrapper={<div className='mt-4 w-full max-w-md' />}
              loadingFallback={<LotSizingInfoSkeleton />}
              message={t('unableToLoad')}
              queryKey={queryKey}
            >
              <LotSizingInfo id={id} />
            </ErrorBoundarySuspense>
            <ErrorBoundarySuspense
              errorFallbackWrapper={<div className='mt-4 w-full max-w-md' />}
              loadingFallback={
                <div className='min-h-40 rounded-xl border bg-card p-6' />
              }
              message={t('unableToLoad')}
              queryKey={queryKey}
            >
              <LotSizingFillRate lotSizingId={id} />
            </ErrorBoundarySuspense>
          </div>
        </div>

        <ErrorBoundarySuspense
          errorFallbackWrapper={<div className='mt-4 w-full max-w-md' />}
          loadingFallback={<LotSizingSupplierTableSkeleton />}
          message={t('unableToLoad')}
          queryKey={queryKey}
        >
          <LotSizingRefsTable lotSizingId={id} />
        </ErrorBoundarySuspense>
      </div>
    </LotSizingPurchaseOrderDraftProvider>
  );
}
